import type { IReaction } from '@/api';
import { Avatar } from '@/features/comments/components/Avatar';
import { CommentActions } from '@/features/comments/components/CommentActions';
import { CommentContent } from '@/features/comments/components/CommentContent';
import { CommentInput } from '@/features/comments/components/CommentInput';
import { useReplyState } from '@/features/comments/hooks/useReplyState';
import type { Comment } from '@/features/comments/types';
import { useTranslation } from 'react-i18next';

interface ReplyItemProps {
	reply: Comment;
	onLike: (data: IReaction) => void;
	onReply: (content: string, isReplyOfReply?: boolean) => void;
}

export const ReplyItem = ({ reply, onLike, onReply }: ReplyItemProps) => {
	const { isHidden, toggleHide, isReplyReplying, toggleReplyReplying } =
		useReplyState();
	const { t } = useTranslation();
	const handleLike = (data: IReaction) => {
		onLike(data);
	};

	const handleReply = () => {
		if (isHidden) toggleHide();
		toggleReplyReplying();
	};

	const handleSubmitReply = (content: string) => {
		onReply(content, true);
		toggleReplyReplying();
	};

	return (
		<div className="relative">
			<div className="flex gap-3">
				<Avatar
					avatar={reply.userAvatar}
					name={reply.userFullName}
					hasReplies={reply.replyCount > 0}
					isHidden={isHidden}
				/>
				<div className="flex-1 pt-1">
					<CommentContent
						author={{
							id: reply.userId,
							name: reply.userFullName,
						}}
						content={reply.content}
					/>

					<CommentActions
						onLike={handleLike}
						onReply={handleReply}
						likes={reply.reactionCount}
						isLiked={reply.reacted}
						createdAt={reply.createdAt}
					/>
				</div>
			</div>
			{isReplyReplying && (
				<div className="mt-3">
					{reply.userFullName && (
						<div className="text-sm mb-1">
							<span className=" text-grey-1 text-sm">
								{t('common:replyingTo')}
							</span>
							<span className="text-sm font-medium">@{reply.userFullName}</span>
						</div>
					)}
					<CommentInput
						onSubmit={handleSubmitReply}
						placeholder="Write a reply..."
						initialValue={`@${reply.userFullName} `}
						author={{
							userId: reply.userId,
							userFullName: reply.userFullName,
							userAvatar: reply.userAvatar ?? '/images/user.png',
						}}
					/>
				</div>
			)}
		</div>
	);
};
